import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { GameStateDB } from '../../../data/datatypes';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';
import { useAutoSaveOnPageLeave } from '../../../store/hooks';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import { GameIndexCardsData, GameIndexCardsState } from '../Games.types';
import IndexCardsCard from './IndexCardsCard';

type Properties = {
    data: GameIndexCardsData;
    setNextButtonDisabled: (disabled: boolean) => void;
};

const IndexCardsGame = (props: Properties) => {
    const { cards, showAll, caption, desc } = props.data;
    const path = useLocation().pathname;
    const dbId: `game_${string}` = `game_${getUrlWithTransientStateProperties(path)}`;

    const [turned, setTurned] = React.useState<boolean[]>(cards.map(() => false));
    const [currentIndex, setCurrentIndex] = React.useState<number>(0);

    // reset internal state when the data changes (fix for displaying the game directly after each other)
    React.useEffect(() => {
        setTurned(cards.map(() => false));
        setCurrentIndex(0);
    }, [props.data]);

    useAutoSaveOnPageLeave(
        [turned, currentIndex],
        async () => {
            const gameState: GameStateDB = {
                _id: dbId,
                state: {
                    turned,
                    currentIndex
                }
            };
            await saveGameState(gameState);
        },
        [path],
        async () => {
            const state = await getGameState(dbId);
            if (state) {
                setTurned((state.state as GameIndexCardsState).turned);
                setCurrentIndex((state.state as GameIndexCardsState).currentIndex);
            }
        }
    );

    // disable next Button conditionally
    React.useEffect(() => {
        if (showAll) {
            // when all cards are shown, one turned card is enough to enable the next-button
            const enabled = turned.some(card => card);
            props.setNextButtonDisabled(!enabled);
        } else {
            // when the cards are shown one after each other, all cards must have been turned
            const disabled = turned.some(card => !card);
            props.setNextButtonDisabled(disabled);
        }
    }, [showAll, turned]);

    const reset = () => {
        setCurrentIndex(0);
        setTurned(cards.map(() => false));
    };

    const clicked = (idxClicked: number) => {
        if (!showAll && currentIndex === idxClicked && turned[idxClicked] === true) {
            if (idxClicked < turned.length - 1) {
                setCurrentIndex(currentIndex + 1);
            }
        } else {
            setTurned(turned.map((t, index) => index === idxClicked ? !t : t));
        }
    };

    let content: JSX.Element | JSX.Element[];
    if (showAll) {
        content = [];
        content = cards.map((card, i) => {
            let isTurned = turned[i];
            let className = 'index-card index-cards-card-' + (isTurned === true ? 'turned' : 'unturned');
            let j = i;

            return <IndexCardsCard
                card={card}
                className={className}
                onClick={() => clicked(j)}
                key={j}
            />;
        });
    } else {
        let card = cards[currentIndex];
        let className = 'index-card index-cards-card-' + (turned[currentIndex] ? 'turned' : 'unturned');
        content = <IndexCardsCard 
            card={card}
            className={className}
            onClick={() => clicked(currentIndex)}
        />;
    }

    return (
        <div className="game">
            <div className="game-content">
                <div className="index-cards">
                    <h1>{caption}</h1>
                    <p><TextWithReferences text={desc || ''} /></p>
                    {content}
                </div>
            </div>
            <div className="game-menu">
                <button onClick={reset} className="primary">Spiel neu starten</button>
            </div>
        </div>
    );
};

export default IndexCardsGame;